import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useTeamAuth } from "@/lib/teamAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamSetup() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { setupPassword } = useTeamAuth();
    const token = params.get("token") || "";
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Missing token. Please use the link from your invitation email.");
            return;
        }
        axios
            .get(`${API}/team/setup-token/${token}`)
            .then((r) => setInfo(r.data))
            .catch((err) => {
                const msg = err?.response?.data?.detail || "Invalid or expired token.";
                setError(typeof msg === "string" ? msg : "Invalid token");
            });
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            await setupPassword(token, password);
            toast.success("All set — welcome to the team.");
            navigate("/team", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not set password";
            toast.error(typeof msg === "string" ? msg : "Setup failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="team-setup-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>

                {error ? (
                    <div className="border border-white/10 bg-ink-800/40 p-8">
                        <h2 className="font-display text-bone text-2xl mb-4">
                            Setup link unavailable
                        </h2>
                        <p className="text-bone-300 text-sm leading-relaxed">{error}</p>
                        <Link
                            to="/team/login"
                            className="mt-6 inline-flex items-center gap-2 text-sm text-ember link-underline"
                        >
                            Already have a password? Sign in
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : !info ? (
                    <div className="flex items-center gap-3 text-bone-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying your invitation…
                    </div>
                ) : (
                    <>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-6">
                            Welcome to the team
                        </div>
                        <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-2">
                            Hi, {info.name}.
                        </h1>
                        <p className="text-bone-300 text-base mb-10">
                            Set a password for{" "}
                            <span className="text-bone">{info.email}</span> to access
                            the manager portal as <span className="text-ember">{info.role}</span>.
                        </p>
                        <form
                            onSubmit={onSubmit}
                            className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                            data-testid="team-setup-form"
                        >
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                    New password
                                </span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 8 characters"
                                    data-testid="team-setup-password"
                                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                    Confirm password
                                </span>
                                <input
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    placeholder="Re-enter it"
                                    data-testid="team-setup-confirm"
                                    className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="team-setup-submit"
                                className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate portal"}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    </>
                )}
            </div>
        </main>
    );
}
